import React, { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { userBootstrapApi } from '../api/bootstrap';
import { cognitoApi } from '../api/api';
import { publicationsApi } from '../api/publications';
import { useAuth } from './AuthContext';
import type { PublicationManifest } from '../utils/biPublication';
import type { ViewDensity } from '../utils/viewDensity';
import { DEFAULT_VIEW_DENSITY, normalizeViewDensity } from '../utils/viewDensity';
import type { PresentationMode } from '../utils/presentationMode';
import { DEFAULT_PRESENTATION_MODE, normalizePresentationMode } from '../utils/presentationMode';
import {
    DEFAULT_MAP_POI_ICON_SIZE,
    normalizeMapPoiIconSize,
    type MapPoiIconSizePreference,
} from '../utils/mapPoiIcons';

// Preference types
export interface TreeSelected {
    devices?: string;
    'single-event'?: string;
    venues?: string;
    [key: string]: string | undefined;
}

export interface ReportFilters {
    [reportType: string]: Record<string, any>;
}

export interface UserPreferences {
    theme?: 'light' | 'dark';
    viewDensity?: ViewDensity;
    presentationMode?: PresentationMode;
    temperatureUnit?: 'fahrenheit' | 'celsius';
    distanceUnit?: 'miles' | 'kilometers';
    mapIconSize?: MapPoiIconSizePreference;
    treeSelected?: TreeSelected;
    reportFilters?: ReportFilters;
    publications?: PublicationManifest[];
    biPublications?: PublicationManifest[];
}

interface UserPreferencesContextType {
    preferences: UserPreferences;
    isLoaded: boolean;
    setTheme: (theme: 'light' | 'dark') => void;
    setViewDensity: (viewDensity: ViewDensity) => void;
    setPresentationMode: (presentationMode: PresentationMode) => void;
    setTemperatureUnit: (temperatureUnit: 'fahrenheit' | 'celsius') => void;
    setDistanceUnit: (distanceUnit: 'miles' | 'kilometers') => void;
    setMapIconSize: (mapIconSize: MapPoiIconSizePreference) => void;
    setTreeSelected: (treeType: string, nodeId: string | undefined) => void;
    setReportFilters: (reportType: string, filters: Record<string, any>) => void;
    setPublications: (publications: PublicationManifest[]) => void;
    upsertPublication: (publication: PublicationManifest) => Promise<PublicationManifest | null>;
    refreshPublications: () => Promise<void>;
    removePublication: (publication: PublicationManifest) => Promise<void>;
    savePreferences: () => Promise<void>;
    loadPreferences: () => Promise<void>;
}

const UserPreferencesContext = createContext<UserPreferencesContextType | undefined>(undefined);

const DEBOUNCE_MS = 2000;
const LOCALSTORAGE_KEY = 'user_preferences_backup';

const readLocalPreferenceBackup = (): UserPreferences => {
    if (typeof window === 'undefined') {
        return {};
    }

    const localBackup = window.localStorage.getItem(LOCALSTORAGE_KEY);
    let localPreferences: UserPreferences = {};
    if (localBackup) {
        try {
            localPreferences = JSON.parse(localBackup);
        } catch (error) {
            console.warn('Failed to parse local preference backup:', error);
        }
    }

    return {
        ...localPreferences,
        viewDensity: normalizeViewDensity(localPreferences.viewDensity),
        presentationMode: normalizePresentationMode(localPreferences.presentationMode),
        mapIconSize: normalizeMapPoiIconSize(localPreferences.mapIconSize),
    };
};

const stripServerBackedPreferences = (preferences: UserPreferences) => {
    const {
        publications,
        biPublications,
        ...serverBackedPreferences
    } = preferences;

    return serverBackedPreferences;
};

export const UserPreferencesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isAuthenticated, isLoading: authLoading } = useAuth();
    const [preferences, setPreferences] = useState<UserPreferences>(() => readLocalPreferenceBackup());
    const [isLoaded, setIsLoaded] = useState(true);
    const [pendingSave, setPendingSave] = useState(false);
    const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const loadedAuthStateRef = useRef<boolean | null>(null);
    const lastSavedServerPreferencesRef = useRef<string>('');
    const serverBackedPreferences = useMemo(
        () => stripServerBackedPreferences(preferences),
        [preferences]
    );
    const serverBackedPreferencesSignature = useMemo(
        () => JSON.stringify(serverBackedPreferences),
        [serverBackedPreferences]
    );

    const refreshPublications = useCallback(async () => {
        try {
            const publications = isAuthenticated ? await publicationsApi.list() : [];
            setPreferences(prev => ({
                ...prev,
                publications,
            }));
        } catch (error) {
            console.warn('Failed to refresh publications:', error);
        }
    }, [isAuthenticated]);

    // Load preferences from backend
    const loadPreferences = useCallback(async () => {
        if (!isAuthenticated) {
            // Load from localStorage only if not authenticated
            const localBackup = localStorage.getItem(LOCALSTORAGE_KEY);
            if (localBackup) {
                try {
                    const localPrefs = JSON.parse(localBackup);
                    setPreferences({
                        ...localPrefs,
                        viewDensity: normalizeViewDensity(localPrefs.viewDensity),
                        presentationMode: normalizePresentationMode(localPrefs.presentationMode),
                    });
                    console.log('Loaded preferences from localStorage (not authenticated)');
                } catch (e) {
                    console.error('Failed to parse localStorage backup:', e);
                }
            }

            setIsLoaded(true);
            loadedAuthStateRef.current = false;
            return;
        }

        try {
            let loadedPrefs: UserPreferences;
            try {
                const bootstrap = await userBootstrapApi.get();
                loadedPrefs = {
                    ...(bootstrap.preferences || {}),
                    publications: bootstrap.publications || [],
                };
            } catch (bootstrapError) {
                console.warn('Failed to load bootstrap preferences, falling back to legacy preference calls:', bootstrapError);
                const response = await cognitoApi.get('/cognito/user-preferences');
                loadedPrefs = response.data.preferences || {};
            }

            // Merge with localStorage backup for non-critical data
            const localBackup = localStorage.getItem(LOCALSTORAGE_KEY);
            if (localBackup) {
                try {
                    const localPrefs = JSON.parse(localBackup);
                    // Cognito preferences take precedence for critical data
                    loadedPrefs.theme = loadedPrefs.theme || localPrefs.theme;
                    loadedPrefs.viewDensity = normalizeViewDensity(loadedPrefs.viewDensity || localPrefs.viewDensity);
                    loadedPrefs.presentationMode = normalizePresentationMode(loadedPrefs.presentationMode || localPrefs.presentationMode);
                    loadedPrefs.temperatureUnit = loadedPrefs.temperatureUnit || localPrefs.temperatureUnit;
                    loadedPrefs.distanceUnit = loadedPrefs.distanceUnit || localPrefs.distanceUnit;
                    loadedPrefs.treeSelected = loadedPrefs.treeSelected || localPrefs.treeSelected;
                    loadedPrefs.publications =
                        loadedPrefs.publications ||
                        loadedPrefs.biPublications ||
                        localPrefs.publications ||
                        localPrefs.biPublications;
                } catch (e) {
                    console.error('Failed to parse localStorage backup:', e);
                }
            }

            loadedPrefs.viewDensity = normalizeViewDensity(loadedPrefs.viewDensity);
            loadedPrefs.presentationMode = normalizePresentationMode(loadedPrefs.presentationMode);
            setPreferences(loadedPrefs);
            console.log('Preferences loaded:', loadedPrefs);
            setIsLoaded(true);
            loadedAuthStateRef.current = true;
            lastSavedServerPreferencesRef.current = JSON.stringify(
                stripServerBackedPreferences(loadedPrefs)
            );

            // Backup to localStorage
            localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(loadedPrefs));

            if (!loadedPrefs.publications && !loadedPrefs.biPublications) {
                void publicationsApi.list()
                    .then((publications) => {
                        setPreferences(prev => ({
                            ...prev,
                            publications,
                        }));
                    })
                    .catch((publicationError) => {
                        console.warn('Failed to load publications from backend:', publicationError);
                    });
            }
        } catch (error) {
            console.error('Failed to load preferences:', error);
            // Fall back to localStorage
            const backup = localStorage.getItem(LOCALSTORAGE_KEY);
            if (backup) {
                const loadedPrefs = JSON.parse(backup);
                setPreferences({
                    ...loadedPrefs,
                    viewDensity: normalizeViewDensity(loadedPrefs.viewDensity),
                    presentationMode: normalizePresentationMode(loadedPrefs.presentationMode),
                });
                console.log('Loaded preferences from localStorage backup');
                lastSavedServerPreferencesRef.current = JSON.stringify(
                    stripServerBackedPreferences(loadedPrefs)
                );
            }
            setIsLoaded(true);
            loadedAuthStateRef.current = true;
        }
    }, [isAuthenticated]);

    // Save preferences to backend (debounced)
    const debouncedSave = useCallback(() => {
        localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(preferences));

        if (!isAuthenticated) {
            return;
        }

        if (serverBackedPreferencesSignature === lastSavedServerPreferencesRef.current) {
            return;
        }

        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        setPendingSave(true);

        saveTimeoutRef.current = setTimeout(async () => {
            try {
                await cognitoApi.post('/cognito/user-preferences', {
                    preferences: serverBackedPreferences
                });
                userBootstrapApi.clear();

                lastSavedServerPreferencesRef.current = serverBackedPreferencesSignature;
                console.log('Preferences saved successfully');
            } catch (error) {
                console.error('Failed to save preferences:', error);
            } finally {
                setPendingSave(false);
            }
        }, DEBOUNCE_MS);
    }, [preferences, isAuthenticated, serverBackedPreferences, serverBackedPreferencesSignature]);

    // Immediate save (for logout/beforeunload)
    const savePreferences = useCallback(async () => {
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        try {
            // Save to localStorage
            localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(preferences));

            // Save to backend only if authenticated and there is a real server-backed change
            if (
                isAuthenticated &&
                serverBackedPreferencesSignature !== lastSavedServerPreferencesRef.current
            ) {
                await cognitoApi.post('/cognito/user-preferences', {
                    preferences: serverBackedPreferences
                });
                userBootstrapApi.clear();
                lastSavedServerPreferencesRef.current = serverBackedPreferencesSignature;
                console.log('Preferences saved immediately');
            }
        } catch (error) {
            console.error('Failed to save preferences:', error);
        }
    }, [preferences, isAuthenticated, serverBackedPreferences, serverBackedPreferencesSignature]);

    // Setters that trigger debounced save
    const setTheme = useCallback((theme: 'light' | 'dark') => {
        setPreferences(prev => {
            const updated = { ...prev, theme };
            return updated;
        });
    }, []);

    const setViewDensity = useCallback((viewDensity: ViewDensity) => {
        setPreferences(prev => ({
            ...prev,
            viewDensity: normalizeViewDensity(viewDensity),
        }));
    }, []);

    const setPresentationMode = useCallback((presentationMode: PresentationMode) => {
        setPreferences(prev => ({
            ...prev,
            presentationMode: normalizePresentationMode(presentationMode),
        }));
    }, []);

    const setTemperatureUnit = useCallback((temperatureUnit: 'fahrenheit' | 'celsius') => {
        setPreferences(prev => ({ ...prev, temperatureUnit }));
    }, []);

    const setDistanceUnit = useCallback((distanceUnit: 'miles' | 'kilometers') => {
        setPreferences(prev => ({ ...prev, distanceUnit }));
    }, []);

    const setMapIconSize = useCallback((mapIconSize: MapPoiIconSizePreference) => {
        setPreferences(prev => ({
            ...prev,
            mapIconSize: normalizeMapPoiIconSize(mapIconSize),
        }));
    }, []);

    const setTreeSelected = useCallback((treeType: string, nodeId: string | undefined) => {
        setPreferences(prev => {
            const updated = {
                ...prev,
                treeSelected: {
                    ...prev.treeSelected,
                    [treeType]: nodeId
                }
            };
            return updated;
        });
    }, []);

    const setReportFilters = useCallback((reportType: string, filters: Record<string, any>) => {
        setPreferences(prev => {
            const updated = {
                ...prev,
                reportFilters: {
                    ...prev.reportFilters,
                    [reportType]: filters
                }
            };
            return updated;
        });
    }, []);

    const setPublications = useCallback((publications: PublicationManifest[]) => {
        setPreferences(prev => ({
            ...prev,
            publications,
        }));
    }, []);

    const upsertPublication = useCallback(async (publication: PublicationManifest) => {
        setPreferences(prev => {
            const currentPublications =
                prev.publications ?? prev.biPublications ?? [];

            return {
                ...prev,
                publications: [
                    ...currentPublications.filter(
                        (existing) => existing.publication.name !== publication.publication.name
                    ),
                    publication,
                ],
            };
        });

        if (isAuthenticated) {
            try {
                const persisted = publication.publication_id
                    ? await publicationsApi.update(publication.publication_id, publication)
                    : await publicationsApi.create(publication);
                userBootstrapApi.clear();

                setPreferences(prev => {
                    const currentPublications =
                        prev.publications ?? prev.biPublications ?? [];
                    return {
                        ...prev,
                        publications: [
                            ...currentPublications.filter(
                                (existing) =>
                                    existing.publication_id !== persisted.publication_id &&
                                    existing.publication.name !== persisted.publication.name
                            ),
                            persisted,
                        ],
                    };
                });
                return persisted;
            } catch (error) {
                console.error('Failed to save publication:', error);
                return null;
            }
        }
        return publication;
    }, [isAuthenticated]);

    const removePublication = useCallback(async (publication: PublicationManifest) => {
        setPreferences(prev => {
            const currentPublications =
                prev.publications ?? prev.biPublications ?? [];

            return {
                ...prev,
                publications: currentPublications.filter(
                    (existing) =>
                        existing.publication_id !== publication.publication_id &&
                        existing.publication.name !== publication.publication.name
                ),
            };
        });

        if (isAuthenticated && publication.publication_id) {
            await publicationsApi.remove(publication.publication_id).catch((error) => {
                console.error('Failed to remove publication:', error);
            });
            userBootstrapApi.clear();
        }
    }, [isAuthenticated]);

    // Auto-load preferences when user becomes authenticated and auth is ready
    useEffect(() => {
        if (!authLoading && loadedAuthStateRef.current !== isAuthenticated) {
            console.log('[UserPreferences] Auth ready, scheduling preferences refresh...');
            const delayMs = isAuthenticated ? 1500 : 0;
            const timer = window.setTimeout(() => {
                loadPreferences();
            }, delayMs);

            return () => window.clearTimeout(timer);
        }
    }, [isAuthenticated, authLoading, loadPreferences]);

    // Trigger save when preferences change (but not on initial load)
    useEffect(() => {
        if (!isLoaded || Object.keys(preferences).length === 0) {
            return;
        }

        if (isAuthenticated && loadedAuthStateRef.current !== true) {
            return;
        }

        debouncedSave();
    }, [preferences, isLoaded, isAuthenticated, debouncedSave]);

    // Save on beforeunload
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (pendingSave) {
                // Use sendBeacon for reliable save on page unload
                const blob = new Blob(
                    [JSON.stringify({ preferences: serverBackedPreferences })],
                    { type: 'application/json' }
                );
                navigator.sendBeacon('/api/cognito/user-preferences', blob);
            }
            // Always save to localStorage
            localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(preferences));
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [preferences, pendingSave, serverBackedPreferences]);

    return (
        <UserPreferencesContext.Provider
            value={{
                preferences: {
                    ...preferences,
                    viewDensity: normalizeViewDensity(preferences.viewDensity ?? DEFAULT_VIEW_DENSITY),
                    presentationMode: normalizePresentationMode(preferences.presentationMode ?? DEFAULT_PRESENTATION_MODE),
                    mapIconSize: normalizeMapPoiIconSize(preferences.mapIconSize ?? DEFAULT_MAP_POI_ICON_SIZE),
                },
                isLoaded,
                setTheme,
                setViewDensity,
                setPresentationMode,
                setTemperatureUnit,
                setDistanceUnit,
                setMapIconSize,
                setTreeSelected,
                setReportFilters,
                setPublications,
                upsertPublication,
                refreshPublications,
                removePublication,
                savePreferences,
                loadPreferences
            }}
        >
            {children}
        </UserPreferencesContext.Provider>
    );
};

export const useUserPreferences = () => {
    const context = useContext(UserPreferencesContext);
    if (!context) {
        throw new Error('useUserPreferences must be used within UserPreferencesProvider');
    }
    return context;
};
